import { makeAutoObservable, runInAction } from 'mobx';
import UsersApiService from '../services/api/UsersApiService';
import {
  AppLanguage,
  DEFAULT_LANGUAGE,
  getStoredLanguage,
  languageToLocale,
  normalizeLanguage,
  persistLanguage,
  SUPPORTED_LANGUAGES,
} from '../shared/i18n/language';
import { translate, TranslationKey } from '../shared/i18n/translations';
import { translateUiText } from '../shared/i18n/runtimeTranslations';
import { getAuthToken } from '../services/api/tokenStorage';

class LanguageStore {
  public language: AppLanguage = getStoredLanguage();
  public supportedLanguages: AppLanguage[] = SUPPORTED_LANGUAGES;
  public isLoading = false;
  public isSaving = false;
  public error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    persistLanguage(this.language);
    this.applyDocumentLanguage();
  }

  public get locale() {
    return languageToLocale(this.language);
  }

  private applyDocumentLanguage() {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = this.locale;
    document.documentElement.dataset.language = this.language;
  }

  public t(key: TranslationKey) {
    return translate(this.language, key);
  }


  public ui(value: unknown) {
    return translateUiText(value, this.language);
  }

  /**
   * Selects the localized version of a statically imported asset.
   * Naming convention used by the project:
   *   Russian: filename.png
   *   English: filename_en.png
   */
  public asset<T>(assets: { ru: T; en?: T }): T {
    return (this.language === 'en' ? assets.en : assets.ru) ?? assets.ru;
  }

  public setLocalLanguage(language: string) {
    const next = normalizeLanguage(language);
    if (this.language === next) return;
    this.language = next;
    persistLanguage(next);
    this.applyDocumentLanguage();
  }

  public setLanguageFromBackend(language?: string | null) {
    this.setLocalLanguage(language || DEFAULT_LANGUAGE);
  }

  public async syncFromBackend(silent = true) {
    if (!getAuthToken()) return;

    if (!silent) {
      runInAction(() => {
        this.isLoading = true;
        this.error = null;
      });
    }

    try {
      const data = await UsersApiService.getLanguageSetting();
      runInAction(() => {
        this.setLanguageFromBackend(data.language);
        this.supportedLanguages =
          (data.supported_languages?.map(normalizeLanguage) as AppLanguage[]) || SUPPORTED_LANGUAGES;
        this.error = null;
      });
    } catch (e: any) {
      runInAction(() => {
        this.error = e?.response?.data?.error || e?.message || 'Failed to sync language';
      });
    } finally {
      if (!silent) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  public async changeLanguage(language: AppLanguage) {
    const next = normalizeLanguage(language);
    const previous = this.language;

    this.setLocalLanguage(next);
    this.isSaving = true;
    this.error = null;

    try {
      if (getAuthToken()) {
        const data = await UsersApiService.updateLanguageSetting(next);
        runInAction(() => {
          this.setLanguageFromBackend(data.language);
          this.supportedLanguages =
            (data.supported_languages?.map(normalizeLanguage) as AppLanguage[]) || SUPPORTED_LANGUAGES;
        });
      }
    } catch (e: any) {
      runInAction(() => {
        this.setLocalLanguage(previous);
        this.error = e?.response?.data?.error || e?.message || 'Failed to save language';
      });
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }
}

export default new LanguageStore();
